import * as Y from 'yjs';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { api, ApiError, base64ToBytes, bytesToBase64, NetworkError } from './api';
import type { Role } from './boardsApi';

export type SyncStatus = 'connecting' | 'synced' | 'syncing' | 'offline' | 'error';

export interface SyncState {
  status: SyncStatus;
  /** Human-readable reason when status is 'error' (403, 404, ...). */
  error: string | null;
  role: Role | null;
  lastSeq: number;
  queued: number;
}

interface DocResponse {
  snapshot: string | null;
  snapshotSeq: number;
  updates: { seq: number; clientId: string; data: string }[];
  seq: number;
  role: Role;
}

interface SinceResponse {
  reset: boolean;
  snapshot?: string;
  updates: { seq: number; clientId: string; data: string }[];
  seq: number;
  pending: number;
}

const FLUSH_DEBOUNCE_MS = 300;
const POLL_ACTIVE_MS = 2000;
const POLL_IDLE_MS = 10000;
const IDLE_AFTER_MS = 30000;
const COMPACT_AFTER = 300;

/**
 * Yjs "provider" over plain HTTP against the PHP update log (docs/02-architecture.md section 3).
 *
 * - connect(): fetch snapshot + updates, apply them, then send whatever the local doc has that
 *   the server lacks (the offline diff). No separate pending queue needs to survive reloads.
 * - Local updates are batched and POSTed; the server echoes them back tagged with our clientId,
 *   which we skip.
 * - Polling backs off when idle and pauses while the tab is hidden.
 * - When the server log grows past COMPACT_AFTER entries, a synced editor uploads a snapshot.
 */
export class HttpProvider {
  readonly store: StoreApi<SyncState>;
  private readonly clientId: string;
  private queue: Uint8Array[] = [];
  private inFlight = false;
  private flushTimer: ReturnType<typeof setTimeout> | undefined;
  private pollTimer: ReturnType<typeof setTimeout> | undefined;
  private retryDelay = 2000;
  private lastActivity = Date.now();
  private destroyed = false;
  private connected = false;

  constructor(
    private readonly doc: Y.Doc,
    private readonly boardId: string,
  ) {
    this.clientId = String(doc.clientID);
    this.store = createStore<SyncState>(() => ({
      status: 'connecting',
      error: null,
      role: null,
      lastSeq: 0,
      queued: 0,
    }));
    doc.on('update', this.onLocalUpdate);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('online', this.onOnline);
  }

  get canEdit(): boolean {
    const role = this.store.getState().role;
    return role === 'owner' || role === 'editor';
  }

  async connect(): Promise<void> {
    if (this.destroyed) return;
    let res: DocResponse;
    try {
      res = await api.get<DocResponse>(`/boards/${this.boardId}/doc`);
    } catch (e) {
      this.handleFailure(e, () => void this.connect());
      return;
    }
    if (this.destroyed) return;

    // Server state as its own doc so we can compute a state vector, then merge into ours.
    const server = new Y.Doc();
    let serverVector: Uint8Array;
    try {
      if (res.snapshot) Y.applyUpdate(server, base64ToBytes(res.snapshot));
      for (const u of res.updates) Y.applyUpdate(server, base64ToBytes(u.data));
      serverVector = Y.encodeStateVector(server);
      Y.applyUpdate(this.doc, Y.encodeStateAsUpdate(server), this);
    } catch (e) {
      console.error('[sync] server document is unreadable', e);
      this.store.setState({ status: 'error', error: 'Server data unreadable' });
      return;
    } finally {
      server.destroy();
    }

    // Whatever we have that the server does not: edits made offline or before this board was synced.
    const diff = Y.encodeStateAsUpdate(this.doc, serverVector);
    if (diff.byteLength > 2 && (res.role === 'owner' || res.role === 'editor')) {
      this.queue.unshift(diff);
    }

    this.connected = true;
    this.retryDelay = 2000;
    this.store.setState({ role: res.role, lastSeq: res.seq, error: null, queued: this.queue.length });
    this.updateStatus();
    void this.flush();
    this.schedulePoll(POLL_ACTIVE_MS);
  }

  private onLocalUpdate = (update: Uint8Array, origin: unknown): void => {
    if (origin === this || this.destroyed) return;
    this.lastActivity = Date.now();
    if (!this.canEdit && this.connected) return; // viewer: keep local, never send
    this.queue.push(update);
    this.store.setState({ queued: this.queue.length });
    this.updateStatus();
    clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => void this.flush(), FLUSH_DEBOUNCE_MS);
  };

  private async flush(): Promise<void> {
    if (this.destroyed || !this.connected || this.inFlight || !this.queue.length || !this.canEdit) return;
    const batch = this.queue.splice(0);
    const merged = batch.length === 1 ? batch[0]! : Y.mergeUpdates(batch);
    this.inFlight = true;
    this.updateStatus();
    try {
      await api.post(`/boards/${this.boardId}/updates`, {
        clientId: this.clientId,
        updates: [bytesToBase64(merged)],
      });
      this.retryDelay = 2000;
    } catch (e) {
      this.queue.unshift(merged);
      this.handleFailure(e, () => void this.flush());
    } finally {
      this.inFlight = false;
      this.store.setState({ queued: this.queue.length });
      this.updateStatus();
    }
    if (this.queue.length) void this.flush();
  }

  private async poll(): Promise<void> {
    if (this.destroyed || !this.connected || document.hidden) return;
    let res: SinceResponse;
    try {
      res = await api.get<SinceResponse>(
        `/boards/${this.boardId}/updates?since=${this.store.getState().lastSeq}`,
      );
    } catch (e) {
      this.handleFailure(e, () => void this.poll());
      return;
    }
    if (this.destroyed) return;

    try {
      this.doc.transact(() => {
        if (res.reset && res.snapshot) Y.applyUpdate(this.doc, base64ToBytes(res.snapshot), this);
        for (const u of res.updates) {
          if (u.clientId === this.clientId) continue;
          Y.applyUpdate(this.doc, base64ToBytes(u.data), this);
        }
      }, this);
    } catch (e) {
      console.error('[sync] bad update from server', e);
      this.store.setState({ status: 'error', error: 'Server data unreadable' });
      return;
    }

    if (res.updates.length) this.lastActivity = Date.now();
    this.store.setState({ lastSeq: Math.max(this.store.getState().lastSeq, res.seq), error: null });
    this.retryDelay = 2000;
    this.updateStatus();

    if (res.updates.length >= 500) {
      // more pages waiting
      this.schedulePoll(0);
      return;
    }
    if (res.pending > COMPACT_AFTER && !this.queue.length && !this.inFlight && this.canEdit && Math.random() < 0.3) {
      void this.compact();
    }
    const idle = Date.now() - this.lastActivity > IDLE_AFTER_MS;
    this.schedulePoll(idle ? POLL_IDLE_MS : POLL_ACTIVE_MS);
  }

  private async compact(): Promise<void> {
    const seq = this.store.getState().lastSeq;
    try {
      await api.post(`/boards/${this.boardId}/snapshot`, {
        seq,
        data: bytesToBase64(Y.encodeStateAsUpdate(this.doc)),
      });
    } catch (e) {
      console.warn('[sync] snapshot failed', e);
    }
  }

  private schedulePoll(ms: number): void {
    clearTimeout(this.pollTimer);
    if (this.destroyed) return;
    this.pollTimer = setTimeout(() => void this.poll(), ms);
  }

  private handleFailure(e: unknown, retry: () => void): void {
    if (this.destroyed) return;
    if (e instanceof ApiError && e.status !== 429 && e.status < 500) {
      // 401/403/404: nothing to retry until the user does something
      this.store.setState({ status: 'error', error: e.message });
      return;
    }
    const wasOffline = this.store.getState().status === 'offline';
    this.store.setState({ status: 'offline', error: e instanceof NetworkError ? null : String(e) });
    if (!wasOffline) console.warn('[sync] offline:', e);
    this.retryDelay = Math.min(this.retryDelay * 2, 60000);
    clearTimeout(this.pollTimer);
    this.pollTimer = setTimeout(retry, this.retryDelay);
  }

  private updateStatus(): void {
    const s = this.store.getState();
    if (s.status === 'error' || s.status === 'offline') return;
    const busy = this.queue.length > 0 || this.inFlight;
    const status: SyncStatus = !this.connected ? 'connecting' : busy ? 'syncing' : 'synced';
    if (status !== s.status) this.store.setState({ status });
  }

  private onVisibility = (): void => {
    if (!document.hidden && this.connected) {
      this.lastActivity = Date.now();
      this.schedulePoll(0);
    }
  };

  private onOnline = (): void => {
    this.retryDelay = 2000;
    if (this.connected) {
      this.store.setState({ status: 'syncing' });
      void this.flush();
      this.schedulePoll(0);
    } else {
      void this.connect();
    }
  };

  destroy(): void {
    this.destroyed = true;
    clearTimeout(this.flushTimer);
    clearTimeout(this.pollTimer);
    this.doc.off('update', this.onLocalUpdate);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('online', this.onOnline);
  }
}
